// Typed calls to the Sprout (staff CRM) API (same site, so the login cookie goes along automatically).

export type Status = 'driving' | 'idling' | 'stopped' | 'offline' | 'no-data';

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'staff';
  mustChangePassword: boolean;
}

export interface Vehicle {
  id: number;
  name: string;
  registration: string | null;
  type: VehicleType;
  customer: CustomerRef | null;
  tracker: { id: number; imei: string } | null;
  trackerStatus: UnitStatus | null;
  status: Status;
  since: string | null;
  lastReceived: string | null;
  position: {
    t: string;
    lat: number;
    lon: number;
    speedKmh: number | null;
    course: number | null;
    valid: boolean;
    satellites: number | null;
    powerV: number | null;
  } | null;
  // Country of the newest position (ISO code), null at sea or without a position.
  country: { code: string; name: string } | null;
  currentJourneyKm: number | null;
  todayKm: number | null;
  // Self-install units only.
  subscription: Subscription | null;
  temperatures: CurrentTemperature[];
  // Fridge running or not, for vehicles with a "fridge" input (null otherwise).
  fridge: FridgeState | null;
}

export interface Place {
  lat: number;
  lon: number;
}

export interface Journey {
  start: string;
  end: string;
  open: boolean;
  endedBy: 'ignition' | 'stop' | 'gap' | 'open';
  durationMin: number;
  distanceKm: number;
  distanceSource: string;
  maxSpeedKmh: number | null;
  idleMin: number;
  // Only for vehicles with a mapped fuel counter; economy only from 10 km.
  fuelL: number | null;
  lPer100Km: number | null;
  from: Place;
  to: Place;
}

export interface Span extends Place {
  start: string;
  end: string | null;
  durationMin: number;
}

export interface Day {
  date: string;
  totalKm: number;
  totalFuelL: number | null;
  lPer100Km: number | null;
  journeys: Journey[];
  stops: Span[];
  idles: Span[];
}

// [time ms, lat, lon, speed km/h, course]
export type TrackPoint = [number, number, number, number | null, number | null];

export class ApiError extends Error {
  // `code` says why, when the server gives one (e.g. 'confirm-server': confirm a second time).
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

// Told when the server says the login has ended (401), e.g. the login was disabled, its
// password reset or a "viewing as" session ran out, so the app can show the login screen.
// Not for requests where 401 means something else (wrong password, expired link…).
let sessionEnded: (() => void) | null = null;
export function onSessionEnded(fn: (() => void) | null): void {
  sessionEnded = fn;
}
const NOT_A_SESSION_CHECK = /^\/login/;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...init?.headers },
    credentials: 'same-origin',
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401 && !NOT_A_SESSION_CHECK.test(path)) sessionEnded?.();
  if (!res.ok) throw new ApiError(res.status, body.error ?? `The server answered ${res.status}.`, body.code);
  return body as T;
}

export type VehicleType = 'truck' | 'van' | 'car' | 'trailer' | 'tractor' | 'plant' | 'other';

export interface TripRules {
  validFrom: string | null; // null: from the very start
  method: 'ignition' | 'movement';
  minStopS: number;
  minTripM: number;
  minTripS: number;
  maxGapS: number;
  startSpeedKmh: number;
  stopRadiusM: number;
  idleS: number;
}

export interface Conversion {
  signed?: 8 | 16 | 32;   // raw value is two's complement of this many bits
  invalid?: number[];     // stored values meaning "no reading" (sensor fault codes)
  bit?: number;
  multiply?: number;
  offset?: number;
  threshold?: number;
  invert?: boolean;
}

export interface SensorRow {
  id: number;
  meaning: string;
  sourceIo: string;
  conversion: Conversion;
  unit: string | null;
  limits?: Limits | null;
  validFrom: string | null;
}

const json = (body: unknown) => ({ body: JSON.stringify(body) });

// Journey rules and input mapping: admins only.
export const admin = {
  saveRules: (id: number, r: TripRules) => request<{ ok: true }>(`/admin/vehicles/${id}/settings`, { method: 'PUT', ...json(r) }),
  saveSensor: (id: number, s: Omit<SensorRow, 'id'>) =>
    request<{ ok: true }>(`/admin/vehicles/${id}/sensors`, { method: 'POST', ...json(s) }),
  deleteSensor: (id: number, sensorId: number) =>
    request<{ ok: true }>(`/admin/vehicles/${id}/sensors/${sensorId}`, { method: 'DELETE' }),
  // Several at once, e.g. an input profile; journeys are recalculated once.
  saveSensors: (id: number, b: { validFrom: string | null; items: Mapping[]; profile?: string }) =>
    request<{ ok: true }>(`/admin/vehicles/${id}/sensors/bulk`, { method: 'POST', ...json(b) }),
  profiles: () => request<{ profiles: InputProfile[] }>('/admin/input-profiles'),
  saveProfile: (p: Omit<InputProfile, 'id' | 'updatedAt' | 'updatedBy'>, id?: number) =>
    request<{ id: number }>(id ? `/admin/input-profiles/${id}` : '/admin/input-profiles', { method: id ? 'PUT' : 'POST', ...json(p) }),
  deleteProfile: (id: number) => request<{ ok: true }>(`/admin/input-profiles/${id}`, { method: 'DELETE' }),
};

// One mapping without its history: what an input profile holds.
export type Mapping = Pick<SensorRow, 'meaning' | 'sourceIo' | 'conversion' | 'unit' | 'limits'>;

// A named set of mappings to apply to a vehicle in one go (a template: applying copies it).
export interface InputProfile {
  id: number;
  name: string;
  model: string | null;
  notes: string | null;
  items: Mapping[];
  updatedAt: string;
  updatedBy: string | null;
}

// --- Trackers, vehicles and installations ---------------------------------------------------

export type UnitStatus = 'ok' | 'sleep' | 'nogps' | 'nodata' | 'nopower';
export type TrackerState = 'stock' | 'allocated' | 'shipped' | 'installed' | 'faulty' | 'retired';
export type DataSource = 'fms' | 'tacho' | 'lvcan' | 'allcan';
export type NetworkType = '2G' | '3G' | '4G' | 'LTE-M' | 'NB-IoT';

export interface CustomerRef {
  id: number;
  name: string;
}

export interface Check {
  level: 'problem' | 'warning' | 'info';
  text: string;
}

export interface TimelineEntry {
  id: number;
  kind: 'comment' | 'auto';
  text: string;
  at: string;
  by: { id: number; name: string } | null;
}

export interface TrackerFields {
  model: string | null;
  serial: string | null;
  firmware: string | null;
  networkType: NetworkType | null;
  simNumber: string | null;
  simActive: boolean | null;
  simIccid: string | null;
  simImsi: string | null;
  simApn: string | null;
  dropIfPowerOff: boolean;
  ignoreGpsAccuracy: boolean;
  notes: string | null;
}

export interface CurrentVehicle {
  installationId: number;
  installedAt: string;
  id: number;
  name: string;
  registration: string | null;
  type: VehicleType;
  customer: CustomerRef | null;
}

export interface TrackerRow extends TrackerFields {
  id: number;
  imei: string;
  state: TrackerState;
  inTraccar: boolean;
  createdAt: string | null;
  lastReceived: string | null;
  lastFix: string | null;
  vehicle: CurrentVehicle | null;
  status?: UnitStatus;
  // Self-install (task 6)
  selfInstall: boolean;
  hasClaimCode: boolean;
  codesCreatedAt: string | null;
  batch: { id: number; name: string | null } | null;
  orderRef: string | null;
  orderEmail: string | null;
  allocatedAt: string | null;
  shippedAt: string | null;
  activatedAt: string | null;
  firstDataAt: string | null;
  // End of the newest subscription period (self-install units).
  paidUntil: string | null;
}

export interface TrackerFilter {
  q?: string;
  state?: TrackerState | '';
  model?: string;
  batch?: string;
  selfInstall?: '' | 'true' | 'false';
}

export interface StockItem {
  imei: string;
  serial?: string | null;
  iccid?: string | null;
  model?: string | null;
}

export type BulkResult = Array<{ id?: number; imei?: string; error?: string }>;

export interface TrackerDetail {
  trackerServer: string;
  tracker: TrackerRow;
  status: UnitStatus;
  checks: Check[];
  recent: { points: number; late: number };
  operator: Operator | null;
  position: { t: string; lat: number; lon: number; valid: boolean } | null;
  installations: Array<{ id: number; installedAt: string; removedAt: string | null; notes: string | null; vehicle: { id: number; name: string } }>;
  timeline: TimelineEntry[];
}

export interface VehicleFields {
  name: string;
  registration: string | null;
  type: VehicleType;
  customerId: number | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  year: number | null;
  voltageSystem: 12 | 24 | null;
  maxSpeedKmh: number;
  dataSources: DataSource[];
}

export interface VehicleDetail {
  trackerServer: string;
  vehicle: VehicleFields & { id: number; createdAt: string | null; customer: CustomerRef | null };
  tracker: {
    id: number;
    imei: string;
    model: string | null;
    simNumber: string | null;
    installationId: number;
    installedAt: string;
    lastReceived: string | null;
    lastFix: string | null;
    status: UnitStatus;
    checks: Check[];
    recent: { points: number; late: number };
    operator: Operator | null;
  } | null;
  position: { t: string; lat: number; lon: number } | null;
  installations: Array<{ id: number; installedAt: string; removedAt: string | null; notes: string | null; tracker: { id: number; imei: string } }>;
  timeline: TimelineEntry[];
  recalculating: boolean;
  latestIo: { t: string; io: Record<string, unknown> } | null;
  settings: Array<TripRules & { savedAt: string | null }>;
  sensors: SensorRow[];
}

export interface Activity {
  from: string;
  to: string;
  lateAfterMin: number;
  cells: Array<{ d: string; h: number; n: number; late: number }>;
  power: Array<{ t: string; kind: 'lost' | 'restored' }>;
}

// Mobile network the tracker is on (from IO 241).
export interface Operator {
  code: string;
  name: string;
  country: string | null; // ISO code, for the flag
  countryName: string | null;
}

export type ValueGroup = 'gps' | 'gsm' | 'power' | 'ignition' | 'can' | 'temperature' | 'other';

export interface LiveValue {
  key: string;
  group: ValueGroup;
  name: string;
  io: string | null;
  value: unknown;
  label: string | null;
  unit: string | null;
  updated: string | null;
  // IO rows: the value as stored (what input mappings convert) and the multiplier that gave `value`.
  raw?: unknown;
  scale?: number | null;
  // Temperature IDs: signed width and fault codes, to start a temperature input right.
  temp?: { signed?: 16 | 32; invalid: number[] } | null;
  // GSM operator only: its country, for the flag.
  country?: string | null;
  countryName?: string | null;
}

export interface LiveValues {
  model: string | null;
  // The model's family, or null when the model isn't set (only shared IDs are named then).
  family: string | null;
  rows: LiveValue[];
}

export interface ModelFamily {
  id: string;
  label: string;
  models: string[];
}

export type Outcome = 'stock' | 'faulty' | 'retired';

export interface InstallRequest {
  trackerId: number;
  vehicleId?: number;
  vehicle?: { name: string; registration: string | null; type: VehicleType; customerId: number | null };
  installedAt: string | null;
  notes: string | null;
  replace?: { trackerState: Outcome };
}

export const trackers = {
  list: (f: TrackerFilter) => request<{ trackers: TrackerRow[] }>(
    `/trackers?${new URLSearchParams(Object.entries(f).filter(([, v]) => v) as Array<[string, string]>)}`),
  get: (id: number) => request<TrackerDetail>(`/trackers/${id}`),
  add: (b: { items: StockItem[]; model: string | null; firmware: string | null; networkType: NetworkType | null; batchName: string | null; selfInstall: boolean }) =>
    request<{ trackerServer: string; batchId: number | null; results: Array<{ imei: string; id?: number; error?: string }> }>('/trackers', { method: 'POST', ...json(b) }),
  save: (id: number, f: Partial<TrackerFields> & { state?: Outcome }) => request<{ ok: true }>(`/trackers/${id}`, { method: 'PATCH', ...json(f) }),
  activity: (id: number, from: string, to: string) => request<Activity>(`/trackers/${id}/activity?from=${from}&to=${to}`),
  values: (id: number, window: '1h' | '24h' | 'all') => request<LiveValues>(`/trackers/${id}/values?window=${window}`),
  // Device page part 2: connections, raw records (+ CSV), chart series.
  connections: (id: number, from: string, to: string) => request<Connections>(`/trackers/${id}/connections?${range(from, to)}`),
  records: (id: number, from: string, to: string) => request<RecordsPage>(`/trackers/${id}/records?${range(from, to)}`),
  recordsCsv: (id: number, from: string, to: string) => download(`/trackers/${id}/records.csv?${range(from, to)}`),
  series: (id: number, from: string, to: string) => request<DeviceSeries>(`/trackers/${id}/series?${range(from, to)}`),
  models: () => request<{ families: ModelFamily[] }>('/tracker-models'),
};

// Self-install stock: claim codes, labels and box inserts, reserving and shipping.
export const stock = {
  batches: () => request<{ batches: Array<{ id: number; name: string; createdAt: string; trackers: number }> }>('/stock/batches'),
  selfInstall: (trackerIds: number[], action: 'prepare' | 'renew' | 'undo') =>
    request<{ results: BulkResult }>('/stock/self-install', { method: 'POST', ...json({ trackerIds, action }) }),
  setState: (trackerIds: number[], to: 'allocated' | 'shipped' | 'stock', orderRef: string | null, orderEmail: string | null) =>
    request<{ results: BulkResult }>('/stock/state', { method: 'POST', ...json({ trackerIds, to, orderRef, orderEmail }) }),
  claimCode: (id: number) => request<{ claimCode: string; link: string }>(`/trackers/${id}/claim-code`),
  // Labels or box inserts as a PDF, saved as a download.
  print: async (trackerIds: number[], kind: 'label' | 'insert', size?: { widthMm: number; heightMm: number }) => {
    const res = await fetch('/api/stock/print', {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ trackerIds, kind, ...(kind === 'label' ? size : {}) }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body.error ?? `The server answered ${res.status}.`);
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kind === 'label' ? 'labels' : 'box-inserts'}-${new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '')}.pdf`;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

export const fleet = {
  vehicle: (id: number) => request<VehicleDetail>(`/vehicles/${id}/detail`),
  saveVehicle: (id: number, f: Partial<VehicleFields>) => request<{ ok: true }>(`/vehicles/${id}`, { method: 'PATCH', ...json(f) }),
  install: (b: InstallRequest) => request<{ installationId: number; vehicleId: number; trackerServer: string }>('/installations', { method: 'POST', ...json(b) }),
  remove: (installationId: number, b: { removedAt: string | null; trackerState: Outcome; notes: string | null }) =>
    request<{ ok: true }>(`/installations/${installationId}/remove`, { method: 'POST', ...json(b) }),
  comment: (subject: 'trackers' | 'vehicles', id: number, text: string) =>
    request<{ ok: true }>(`/${subject}/${id}/timeline`, { method: 'POST', ...json({ text }) }),
  deleteComment: (entryId: number) => request<{ ok: true }>(`/timeline/${entryId}`, { method: 'DELETE' }),
};

export interface Customer {
  id: number;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  createdAt: string | null;
  vehicles?: number;
}

export type CustomerInput = Pick<Customer, 'name' | 'contactName' | 'phone' | 'email' | 'notes'>;

export const customers = {
  list: (q = '') => request<{ customers: Customer[] }>(`/customers${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  get: (id: number) => request<{ customer: Customer; vehicles: Vehicle[] }>(`/customers/${id}`),
  add: (c: CustomerInput) => request<{ id: number }>('/customers', { method: 'POST', ...json(c) }),
  update: (id: number, c: CustomerInput) => request<{ ok: true }>(`/customers/${id}`, { method: 'PATCH', ...json(c) }),
};

export interface UserRow {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'staff' | 'customer';
  disabled: boolean;
  mustChangePassword: boolean;
  createdAt: string | null;
  lastLogin: string | null;
  activeSessions: number;
  // The user deleted their account in the app: restorable until wipeAfter, then wiped (deletedAt).
  deletionRequestedAt?: string | null;
  deletedAt?: string | null;
  wipeAfter?: string | null;
}

export const users = {
  list: () => request<{ minPassword: number; users: UserRow[] }>('/admin/users'),
  // Customer app logins.
  forCustomer: (customerId: number) => request<{ minPassword: number; users: UserRow[] }>(`/customers/${customerId}/users`),
  addForCustomer: (customerId: number, u: { email: string; name: string; password: string }) =>
    request<{ id: number }>(`/customers/${customerId}/users`, { method: 'POST', ...json(u) }),
  setDisabled: (u: UserRow, disabled: boolean) =>
    request<{ ok: true }>(`/admin/users/${u.id}`, { method: 'PATCH', ...json({ name: u.name, role: u.role, disabled }) }),
  // A one-time link (valid for a minute) that opens the customer app as this user.
  viewAs: (id: number) => request<{ url: string }>(`/users/${id}/impersonate`, { method: 'POST' }),
  cancelDeletion: (id: number) => request<{ ok: true }>(`/users/${id}/cancel-deletion`, { method: 'POST' }),
  add: (u: { email: string; name: string; role: 'admin' | 'staff'; password: string }) =>
    request<{ id: number }>('/admin/users', { method: 'POST', ...json(u) }),
  update: (id: number, u: { name: string; role: 'admin' | 'staff'; disabled: boolean }) =>
    request<{ ok: true }>(`/admin/users/${id}`, { method: 'PATCH', ...json(u) }),
  resetPassword: (id: number, password: string) =>
    request<{ ok: true }>(`/admin/users/${id}/password`, { method: 'POST', ...json({ password }) }),
  changeOwnPassword: (current: string, password: string) =>
    request<{ ok: true }>('/me/password', { method: 'POST', ...json({ current, password }) }),
};

export const MIN_PASSWORD = 12;

// A random password that is easy to read out or type: no 0/O, 1/l/I.
export function generatePassword(length = 16): string {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(length));
  return Array.from(bytes, (b) => chars[b % chars.length]).join('').replace(/(.{4})(?=.)/g, '$1-');
}

export const api = {
  me: () => request<{ user: User }>('/me'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/logout', { method: 'POST' }),
  vehicles: () => request<{ vehicles: Vehicle[] }>('/vehicles'),
  day: (id: number, date: string) => request<Day>(`/vehicles/${id}/day?date=${date}`),
  track: (id: number, from: string, to: string) =>
    request<{ points: TrackPoint[] }>(`/vehicles/${id}/track?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  temperatures: (id: number, from: string, to: string) =>
    request<{ sensors: TemperatureSeries[]; fridge: FridgeState | null }>(`/vehicles/${id}/temperatures?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  journey: (id: number, start: string) =>
    request<Journey & { track: TrackPoint[] }>(`/vehicles/${id}/journeys/${encodeURIComponent(start)}`),
};

// --- Subscriptions of self-install trackers (task 6, phase 3) ---------------------------------

export type SubStatus = 'active' | 'expiring' | 'grace' | 'expired';
export type SubFilter = 'attention' | SubStatus | 'all';

export interface Subscription {
  paidUntil: string;
  status: SubStatus;
  // When the customer app stops showing the vehicle (paid until + grace days).
  hiddenFrom: string;
}

export interface SubscriptionRow {
  trackerId: number;
  imei: string;
  model: string | null;
  activatedAt: string;
  lastReceived: string | null;
  vehicle: { id: number; name: string; registration: string | null } | null;
  customer: { id: number; name: string } | null;
  subscription: Subscription | null;
  // The customer deleted its last app login (API v1): remove the tracker, as for expired ones.
  accountClosedAt?: string | null;
}

export interface SubscriptionPeriod {
  id: number;
  start: string;
  end: string;
  source: 'initial_purchase' | 'manual_staff' | 'stripe';
  amountCents: number | null;
  currency: string | null;
  notes: string | null;
  createdAt: string;
  by: { id: number; name: string; customer: boolean } | null;
  customer: { id: number; name: string };
  removable: boolean;
}

export const subscriptions = {
  list: (filter: SubFilter) =>
    request<{ graceDays: number; expiringDays: number; counts: Record<SubStatus, number> & { closedActive?: number }; trackers: SubscriptionRow[] }>(`/subscriptions?filter=${filter}`),
  ofTracker: (id: number) =>
    request<{ graceDays: number; subscription: Subscription | null; nextStart: string; periods: SubscriptionPeriod[] }>(`/trackers/${id}/subscriptions`),
  renew: (id: number, body: { years: number; startToday?: boolean; amountCents?: number | null; notes?: string | null }) =>
    request<{ id: number; start: string; end: string }>(`/trackers/${id}/renew`, { method: 'POST', ...json(body) }),
  remove: (periodId: number) => request<{ ok: true }>(`/subscriptions/${periodId}`, { method: 'DELETE' }),
};

// --- Our own tracker gateway (replacing Traccar in stages) -----------------------------------

export interface GatewayUnit { trackerId: number; imei: string; vehicle: { id: number; name: string } | null }
export interface GatewaySession {
  id: number; mode: 'shadow' | 'primary'; ip: string; imei: string | null; unit: GatewayUnit | null;
  startedAt: string; endedAt: string | null; endReason: string | null; packets: number; records: number; flags: string[];
}
export interface GatewayOverview {
  runs: Array<{ day: string; ranAt: string; ours: number; traccar: number; matched: number; onlyOurs: number; onlyTraccar: number;
    different: number; clean: boolean; examples: Array<{ kind: string; trackerId: number; fixTime: string; fields?: string[] }> }>;
  live: GatewaySession[];
  flagged: GatewaySession[];
  refusals: Array<{ at: string; ip: string; imei: string | null; reason: string }>;
  refused24h: { n: number; ips: number };
  blocks: Array<{ ip: string; blockedAt: string; until: string; times: number; reason: string }>;
  quarantine: Array<{ id: number; reason: string; receivedAt: string; unit: GatewayUnit | null; fixTime: string; lat: number; lon: number; speedKmh: number | null }>;
}

export const gateway = {
  overview: () => request<GatewayOverview>('/gateway'),
  decide: (ids: number[], decision: 'released' | 'deleted') =>
    request<{ done: number }>('/gateway/quarantine', { method: 'POST', ...json({ ids, decision }) }),
  unblock: (ip: string) => request<{ done: number }>('/gateway/unblock', { method: 'POST', ...json({ ip }) }),
};

export interface GatewayCapacity {
  mode: string; max: number; binding: string; warnAt: number; warnShare: number;
  limits: Array<{ key: string; label: string; max: number; detail: string }>;
  connections: number; trackers: number; packetsPerSec: number; packetsPerSecMax: number;
  process: { rssMb: number; cpuPct: number; loopPct: number; uptimeS: number; fdLimit: number };
}
export interface GatewayAlert { level: 'ok' | 'warn' | 'down'; message: string | null }
export interface GatewayMetrics {
  capacity: GatewayCapacity | null;
  alert: GatewayAlert;
  coveredHours: number;
  totals: { bytesIn: number; bytesOut: number; packets: number; records: number };
  hourly: Array<{ t: string; bytesIn: number; bytesOut: number; packets: number; records: number; refused: number }>;
  connections: Array<{ t: string; connections: number; trackers: number; rssMb: number; cpuPct: number; max: number }>;
  trackers: Array<{ unit: GatewayUnit | null; model: string | null; packets: number; records: number; bytes: number; connections: number; lastAt: string; mobileBytes: number }>;
  overhead: { perPacket: number; perConnection: number };
}
export const gatewayStatus = {
  metrics: () => request<GatewayMetrics>('/gateway/metrics'),
  alert: () => request<GatewayAlert>('/gateway/alert'),
};

// Temperature inputs ("temperature:<name>" inputs) and fridge setpoints ("setpoint:<name>")
// with their newest reading.
export interface Limits { min?: number; max?: number }
export type TempKind = 'temperature' | 'setpoint';
export interface CurrentTemperature {
  kind: TempKind;
  name: string;
  unit: string;
  value: number | null;      // null: no reading (sensor fault or nothing sent lately)
  at: string | null;
  limits: Limits | null;
  state: 'ok' | 'low' | 'high' | 'none';
}
export interface TemperatureSeries {
  kind: TempKind;
  name: string;
  unit: string;
  limits: Limits | null;
  points: Array<[number, number]>;
  low: number | null;
  high: number | null;
  outside: number;
}
export interface FridgeState {
  on: boolean | null;       // null: no reading lately
  at: string | null;        // newest reading
  since: string | null;     // first reading of the current state (within 7 days)
}

const range = (from: string, to: string) => `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;

// A file from the API (e.g. a CSV), saved by the browser under the server's file name.
async function download(path: string): Promise<void> {
  const res = await fetch(`/api${path}`, { credentials: 'same-origin' });
  if (!res.ok) {
    let msg = `Download failed (${res.status}).`;
    try { msg = ((await res.json()) as { error?: string }).error ?? msg; } catch { /* not JSON */ }
    throw new Error(msg);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'download';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface Connection {
  id: number; mode: 'shadow' | 'primary'; ip: string; start: string; end: string | null; endReason: string | null;
  packets: number; records: number; bytesIn: number; flags: string[];
}
export interface Connections { knownSince: string | null; sessions: Connection[]; arrivals: Array<[number, number]> }

export interface RawRecord {
  fixTime: string; received: string | null; lat: number; lon: number; speedKmh: number | null; course: number | null;
  altitudeM: number | null; satellites: number | null; hdop: number | null; valid: boolean; powerV: number | null; source: string;
  io: Record<string, unknown>;
}
export interface RecordsPage { more: boolean; limit: number; io: Array<{ io: string; name: string | null }>; records: RawRecord[] }

export interface DeviceSeries {
  records: number;
  gsm: Array<[number, number]>;
  power: Array<[number, number]>;
  battery: Array<[number, number]>;
  satellites: Array<[number, number]>;
  hdop: Array<[number, number]>;
  noPowerBelowV: number;
  gpsErrors: { count: number; invalid: number; latest: Array<{ t: string; valid: boolean; satellites: number | null }> };
}

// Products fitted to a vehicle, and attachments about a vehicle or a tracker (device page part 2).
export interface Product {
  id: number; kind: string; model: string | null; serial: string | null; notes: string | null;
  installedOn: string | null; removedOn: string | null; createdAt: string | null; createdBy: string | null;
}
export type ProductInput = Partial<Pick<Product, 'kind' | 'model' | 'serial' | 'notes' | 'installedOn' | 'removedOn'>>;
export interface Attachment {
  id: number; fileName: string; contentType: string; sizeBytes: number; note: string | null;
  uploadedAt: string | null; uploadedBy: string | null; uploadedById: number | null;
}
export const extras = {
  products: (vehicleId: number) => request<{ kinds: Record<string, string>; products: Product[] }>(`/vehicles/${vehicleId}/products`),
  addProduct: (vehicleId: number, p: ProductInput) => request<Product>(`/vehicles/${vehicleId}/products`, { method: 'POST', ...json(p) }),
  saveProduct: (id: number, p: ProductInput) => request<Product>(`/products/${id}`, { method: 'PATCH', ...json(p) }),
  deleteProduct: (id: number) => request<{ ok: true }>(`/products/${id}`, { method: 'DELETE' }),
  attachments: (subject: 'vehicles' | 'trackers', id: number) => request<{ attachments: Attachment[] }>(`/${subject}/${id}/attachments`),
  // The file goes as the raw body; its name and note in the address.
  attach: (subject: 'vehicles' | 'trackers', id: number, file: File, note: string) =>
    request<Attachment>(`/${subject}/${id}/attachments?name=${encodeURIComponent(file.name)}${note ? `&note=${encodeURIComponent(note)}` : ''}`,
      { method: 'POST', body: file, headers: { 'content-type': file.type || 'application/octet-stream' } }),
  deleteAttachment: (id: number) => request<{ ok: true }>(`/attachments/${id}`, { method: 'DELETE' }),
  fileUrl: (id: number) => `/api/attachments/${id}/file`,
};

// Commands to a tracker, sent by our gateway once it is in charge (device page part 2).
export interface CommandRow {
  id: number; command: string; status: 'queued' | 'sent' | 'answered' | 'failed' | 'expired' | 'cancelled';
  createdAt: string | null; sentAt: string | null; answeredAt: string | null; response: string | null; error: string | null; by: string | null;
}
export const commands = {
  list: (trackerId: number) => request<{ available: boolean; gatewayMode: 'shadow' | 'primary' | null; presets: Record<string, string>; history: CommandRow[] }>(`/trackers/${trackerId}/commands`),
  send: (trackerId: number, b: { command: string; confirmed?: boolean; serverConfirmed?: boolean }) =>
    request<CommandRow>(`/trackers/${trackerId}/commands`, { method: 'POST', ...json(b) }),
  cancel: (id: number) => request<{ ok: true }>(`/commands/${id}/cancel`, { method: 'POST' }),
};

// --- Geofences ---------------------------------------------------------------------------

export const GEOFENCE_COLOURS = ['blue', 'green', 'orange', 'purple', 'red', 'teal'] as const;
export type GeofenceColour = typeof GEOFENCE_COLOURS[number];

export interface Geofence {
  id: number;
  name: string;
  colour: GeofenceColour;
  customer: CustomerRef | null;   // null: company-wide (staff only)
  kind: 'circle' | 'polygon';
  circle: { lat: number; lon: number; radiusM: number } | null;
  points: Array<[number, number]>;  // [lon, lat], the outline (circles too)
  areaM2: number;
  insideNow: number;
  activeFrom: string;
  archivedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface GeofenceEvent {
  kind: 'enter' | 'exit';
  at: string;
  passedThrough: boolean;
}

export interface VehicleGeofenceEvent extends GeofenceEvent {
  lastOtherSideAt: string | null;
  lat: number;
  lon: number;
  geofence: { id: number; name: string; colour: GeofenceColour; removed: boolean };
}

export interface GeofenceInput {
  name?: string;
  colour?: GeofenceColour;
  customerId?: number | null;
  circle?: { lat: number; lon: number; radiusM: number };
  points?: Array<[number, number]>;
}

export const geofences = {
  list: (q = '', customerId: number | 'none' | null = null) => request<{ geofences: Geofence[] }>(`/geofences?${new URLSearchParams({
    ...(q ? { q } : {}), ...(customerId !== null ? { customerId: String(customerId) } : {}) })}`),
  get: (id: number) => request<Geofence & { events: Array<GeofenceEvent & { vehicle: { id: number; name: string } }> }>(`/geofences/${id}`),
  add: (g: GeofenceInput) => request<{ id: number }>('/geofences', { method: 'POST', ...json(g) }),
  update: (id: number, g: GeofenceInput) => request<{ ok: true }>(`/geofences/${id}`, { method: 'PATCH', ...json(g) }),
  remove: (id: number) => request<{ ok: true }>(`/geofences/${id}/archive`, { method: 'POST' }),
  vehicleEvents: (vehicleId: number, from: string, to: string) =>
    request<{ events: VehicleGeofenceEvent[] }>(`/vehicles/${vehicleId}/geofence-events?${new URLSearchParams({ from, to })}`),
  searchPlaces: (q: string) => request<{ results: Array<{ label: string; lat: number; lon: number }> }>(`/geocode/search?${new URLSearchParams({ q })}`),
};

// Tachograph files (.ddd): uploaded by staff now, downloaded remotely later. The server decodes
// a file each time it is opened; times in it are UTC, as the tachograph keeps them.
export type TachoActivity = 'rest' | 'available' | 'work' | 'driving' | 'unknown';
export type TachoTotals = Record<TachoActivity, number>;
export interface TachoSegment {
  from: number; to: number; kind: TachoActivity; slot: 'driver' | 'co-driver'; crew: boolean; cardInserted: boolean;
}
export interface TachoCardSession {
  slot: 'driver' | 'co-driver'; name: string; cardNumber: string; cardNation: string | null;
  inserted: string | null; withdrawn: string | null; odometerIn: number | null; odometerOut: number | null; manualEntries: boolean;
}
export interface TachoDay {
  date: string; distanceKm: number | null; odometerMidnight?: number | null; presenceCounter?: number | null;
  segments: TachoSegment[]; totals: TachoTotals; coDriverTotals?: TachoTotals; cards?: TachoCardSession[]; vehicles?: string[];
  noData?: boolean;                        // driver timelines: no file covers this day
}
export type TachoSeverity = 'MI' | 'SI' | 'VSI' | 'MSI';
export interface TachoInfringement {
  code: string; article: string; title: string; detail: string;
  at: string; from: string | null; to: string | null; value: number; limit: number;
  severity: TachoSeverity | null; severityLabel: string | null;
}
export interface TachoDriver {
  id: number; name: string; cardNumber: string; cardNation: string | null; customerId: number | null; customerName: string | null;
  dataFrom: string | null; dataTo: string | null; infringements: number; analysedAt: string | null; lastCardDownload: string | null; files?: number;
}
export type TachoDriverDay = TachoDay & { infringements: TachoInfringement[] };
export interface TachoShift {
  start: string; end: string; drivingMin: number;
  rest: { start: string; end: string; minutes: number; kind: 'regular' | 'split' | 'reduced' | 'too-short'; weekly: 'regular' | 'reduced' | null };
}
export type TachoGeneration = 'gen1' | 'gen2v1' | 'gen2v2';
export interface TachoVehicleUse {
  reg: string; nation: string | null; vin: string | null; firstUse: string | null; lastUse: string | null; odometerBegin: number | null; odometerEnd: number | null;
}
export type TachoDecoded = {
  generation: TachoGeneration; signaturesChecked: boolean; period: { from: string | null; to: string | null }; days: TachoDay[]; warnings: string[];
} & ({
  kind: 'card';
  card: {
    number: string; nation: string | null; issuingAuthority: string; issued: string | null; validFrom: string | null; expires: string | null;
    surname: string; firstNames: string; birthDate: string | null; language: string;
    licence: { number: string; nation: string | null; authority: string } | null; lastDownload: string | null;
  };
  vehiclesUsed: TachoVehicleUse[];
} | {
  kind: 'vu';
  vehicle: { vin: string; reg: string; nation: string | null };
  unit: {
    manufacturer: string | null; partNumber: string | null; serialNumber: string | null; softwareVersion: string | null; approvalNumber: string | null;
    currentTime: string | null; downloadableFrom: string | null; downloadableTo: string | null;
    lastDownload: { at: string | null; cardNumber: string; by: string } | null;
    companyLocks: Array<{ company: string; cardNumber: string; from: string | null; to: string | null }>;
  };
});
export interface TachoFile {
  id: number; kind: 'card' | 'vu'; generation: TachoGeneration; source: 'upload' | 'remote'; fileName: string; sizeBytes: number;
  customerId: number | null; customerName: string | null; vehicleId: number | null; vehicleName: string | null; vehicleRegistration: string | null;
  cardNumber: string | null; driverName: string | null; vin: string | null; registration: string | null;
  periodFrom: string | null; periodTo: string | null; downloadedAt: string | null; note: string | null;
  uploadedAt: string | null; uploadedBy: string | null;
}
export interface TachoFilter { q?: string; kind?: 'card' | 'vu' | ''; customerId?: number | 'none' | null; vehicleId?: number | null }

export const tacho = {
  list: (f: TachoFilter = {}) => request<{ files: TachoFile[] }>(`/tacho/files?${new URLSearchParams({
    ...(f.q ? { q: f.q } : {}), ...(f.kind ? { kind: f.kind } : {}),
    ...(f.customerId ? { customerId: String(f.customerId) } : {}), ...(f.vehicleId ? { vehicleId: String(f.vehicleId) } : {}) })}`),
  get: (id: number) => request<{
    file: TachoFile; decoded: TachoDecoded | null; error?: string;
    infringements?: Record<string, TachoInfringement[]>; drivers?: Array<{ id: number; name: string; role: 'holder' | 'vu' }>;
  }>(`/tacho/files/${id}`),
  // The file goes as the raw body; its name (and a customer for card files) in the address.
  upload: (file: File, customerId: number | null) =>
    request<{ file: TachoFile; duplicate: boolean }>(`/tacho/files?${new URLSearchParams({ name: file.name, ...(customerId ? { customerId: String(customerId) } : {}) })}`,
      { method: 'POST', body: file, headers: { 'content-type': 'application/octet-stream' } }),
  file: (id: number, body: { customerId?: number | null; vehicleId?: number | null; note?: string | null }) =>
    request<{ file: TachoFile }>(`/tacho/files/${id}`, { method: 'PATCH', ...json(body) }),
  remove: (id: number) => request<{ ok: true }>(`/tacho/files/${id}`, { method: 'DELETE' }),
  downloadUrl: (id: number) => `/api/tacho/files/${id}/file`,
  drivers: (q = '', customerId: number | 'none' | null = null) => request<{ drivers: TachoDriver[] }>(`/tacho/drivers?${new URLSearchParams({
    ...(q ? { q } : {}), ...(customerId ? { customerId: String(customerId) } : {}) })}`),
  driver: (id: number) => request<{
    driver: TachoDriver; files: Array<TachoFile & { role: 'holder' | 'vu' }>; from: string | null; to: string | null; days: TachoDriverDay[]; shifts: TachoShift[];
  }>(`/tacho/drivers/${id}`),
  fileDriver: (id: number, customerId: number | null) => request<{ ok: true }>(`/tacho/drivers/${id}`, { method: 'PATCH', ...json({ customerId }) }),
};
